import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { listContexts, openConversation, sendMessage, sendMessageWithImage } from "../../api/agent";
import { useAutoGrowTextarea } from "../../hooks/useAutoGrowTextarea";
import HelpLink, { GUIDE_URLS } from "@/components/HelpLink";
import { imageFilesFromClipboard } from "./clipboard";
import MessageImage from "./MessageImage";

const CONTEXT_BADGE = {
  support_equipe: { label: "Support & diagnostic", color: "bg-emerald-100 text-emerald-800" },
  idees_evolutions: { label: "Idées & évolutions", color: "bg-amber-100 text-amber-800" },
  cadrage_evolutions: { label: "Cadrage", color: "bg-indigo-100 text-indigo-800" },
};

export default function AgentChat({ token, onBackToDashboard, canAccessDossiers = true, onLogout }) {
  const { contextName } = useParams();
  const navigate = useNavigate();
  const [contexts, setContexts] = useState([]);
  const [persona, setPersona] = useState("unknown");
  const [conv, setConv] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingInit, setLoadingInit] = useState(true);
  const [pendingImages, setPendingImages] = useState([]); // [{ file, url }]
  const pendingImagesRef = useRef([]);
  const [urgent, setUrgent] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useAutoGrowTextarea(input, { maxHeight: "40vh" });

  const stageImages = (files) => {
    const candidates = Array.from(files || []).filter(Boolean);
    if (candidates.length === 0) return;
    const available = Math.max(0, 5 - pendingImagesRef.current.length);
    const additions = candidates.slice(0, available).map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));
    const next = [...pendingImagesRef.current, ...additions];
    pendingImagesRef.current = next;
    setPendingImages(next);
    if (candidates.length > available) {
      toast.error("5 captures maximum par message");
    }
  };
  const removePendingImage = (index) => {
    const current = pendingImagesRef.current;
    if (current[index]?.url) URL.revokeObjectURL(current[index].url);
    const next = current.filter((_, candidateIndex) => candidateIndex !== index);
    pendingImagesRef.current = next;
    setPendingImages(next);
  };
  const clearPendingImages = () => {
    pendingImagesRef.current.forEach(({ url }) => URL.revokeObjectURL(url));
    pendingImagesRef.current = [];
    setPendingImages([]);
  };
  const onPaste = (e) => {
    const files = imageFilesFromClipboard(e.clipboardData);
    if (files.length > 0) { e.preventDefault(); stageImages(files); }
  };

  useEffect(() => () => {
    pendingImagesRef.current.forEach(({ url }) => URL.revokeObjectURL(url));
    pendingImagesRef.current = [];
  }, []);

  // Load contexts list always (for the picker UI when no contextName)
  useEffect(() => {
    listContexts(token)
      .then((d) => {
        setContexts(d.contexts || []);
        setPersona(d.persona || "unknown");
      })
      .catch((e) => {
        console.error(e);
        // 403 = l'assistant est réservé à l'équipe. Ce n'est pas une panne :
        // le dire « impossible de charger » enverrait réessayer pour rien.
        if (e?.response?.status === 403) {
          toast.error(e?.response?.data?.detail || "L'assistant est réservé à l'équipe");
          if (canAccessDossiers) navigate("/");
          return;
        }
        toast.error("Impossible de charger l'assistant");
      });
  }, [token, navigate, canAccessDossiers]);

  // Load conversation when context selected
  useEffect(() => {
    if (!contextName) {
      setConv(null);
      setLoadingInit(false);
      return;
    }
    setLoadingInit(true);
    openConversation(token, contextName)
      .then((c) => setConv(c))
      .catch((e) => {
        console.error(e);
        toast.error(e?.response?.data?.detail || "Erreur d'ouverture de la conversation");
      })
      .finally(() => setLoadingInit(false));
  }, [contextName, token]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conv?.messages?.length]);

  const sendUserMessage = async ({
    message,
    images = [],
    isUrgent = false,
    clearComposer = false,
  }) => {
    if (sending || !contextName) return;
    if (!message.trim() && images.length === 0) return;
    if (clearComposer) {
      setInput("");
      clearPendingImages();
      setUrgent(false);
    }
    setSending(true);
    setConv((prev) => prev ? ({
      ...prev,
      messages: [
        ...(prev.messages || []),
        { role: "user", content: message, timestamp: new Date().toISOString(), urgent: isUrgent },
      ],
    }) : prev);
    try {
      const updated = images.length > 0
        ? await sendMessageWithImage(
            token,
            contextName,
            message,
            images.map(({ file }) => file),
            isUrgent,
          )
        : await sendMessage(token, contextName, message, isUrgent);
      setConv(updated);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Erreur d'envoi");
      openConversation(token, contextName).then(setConv).catch(() => {});
    } finally {
      setSending(false);
    }
  };

  const onSend = () => {
    sendUserMessage({
      message: input,
      images: pendingImages,
      isUrgent: urgent,
      clearComposer: true,
    });
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  // ============ Render: contexts picker ============
  if (!contextName) {
    return (
      <div className="agent-picker max-w-3xl mx-auto p-6">
        <div className="agent-picker-header mb-8 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold mb-1">Assistant</h1>
            <p className="text-sm text-gray-600">
              Choisis le mode qui correspond à ce que tu veux faire.
            </p>
            {persona !== "unknown" && (
              <p className="text-xs text-gray-500 mt-2">
                Connecté en tant que : <span className="font-medium capitalize">{persona}</span>
              </p>
            )}
          </div>
          <HelpLink url={GUIDE_URLS.assistant} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {contexts.map((c) => {
            const badge = CONTEXT_BADGE[c.name] || { label: c.label, color: "bg-gray-100 text-gray-800" };
            return (
              <button
                key={c.name}
                onClick={() => navigate(`/assistant/${c.name}`)}
                className="agent-context-card text-left p-5 border rounded-lg hover:shadow-md transition bg-white"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs px-2 py-1 rounded ${badge.color}`}>{badge.label}</span>
                  {c.status === "completed" ? (
                    <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-800">
                      ✓ Terminé
                    </span>
                  ) : c.n_answered > 0 && c.n_questions > 0 ? (
                    <span className="text-xs text-gray-500">
                      {c.n_answered}/{c.n_questions} répondues
                    </span>
                  ) : c.n_questions > 0 ? (
                    <span className="text-xs text-gray-500">{c.n_questions} questions</span>
                  ) : null}
                </div>
                <p className="text-sm text-gray-700">{c.description}</p>
                {c.status === "completed" && (
                  <p className="text-xs text-orange-600 mt-2">
                    → Clique pour modifier / compléter tes réponses
                  </p>
                )}
                {c.status === "active" && c.n_answered > 0 && (
                  <p className="text-xs text-orange-600 mt-2">
                    → Clique pour continuer
                  </p>
                )}
              </button>
            );
          })}
        </div>
        <div className="mt-6">
          <button
            onClick={() => navigate("/mes-demandes")}
            className="text-sm text-orange-600 hover:text-orange-700 font-medium"
          >
            📋 Voir mes demandes →
          </button>
        </div>
        <div className="mt-8">
          {canAccessDossiers ? (
            <button
              onClick={() => {
                // Sans ce reset, la page "/" re-rend l'écran d'où l'on venait
                // (appState inchangé) : boucle Sources → assistant → Sources.
                if (onBackToDashboard) onBackToDashboard();
                navigate("/");
              }}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              ← Retour au dashboard
            </button>
          ) : (
            <button onClick={onLogout} className="text-sm text-gray-600 hover:text-gray-900">
              Se déconnecter
            </button>
          )}
        </div>
      </div>
    );
  }

  // ============ Render: chat ============
  const badge = CONTEXT_BADGE[contextName] || { label: contextName, color: "bg-gray-100 text-gray-800" };
  const isCompleted = conv?.status === "completed";
  const collectedCount = conv?.collected_answers ? Object.keys(conv.collected_answers).length : 0;

  return (
    <div className="agent-chat-page flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <header className="agent-chat-header border-b bg-white px-6 py-3 flex items-center justify-between">
        <div className="agent-chat-title-row flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate("/assistant")}
            className="shrink-0 text-sm text-gray-600 hover:text-gray-900"
          >
            ←
          </button>
          <div className="min-w-0">
            <div className="agent-chat-badges flex items-center gap-2">
              <span className={`text-xs px-2 py-1 rounded ${badge.color}`}>{badge.label}</span>
              {isCompleted && (
                <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-800">
                  Terminé ✓
                </span>
              )}
            </div>
            {collectedCount > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                {collectedCount} réponse{collectedCount > 1 ? "s" : ""} enregistrée{collectedCount > 1 ? "s" : ""}
              </p>
            )}
          </div>
        </div>
        <HelpLink url={GUIDE_URLS.assistant} />
      </header>

      {/* Messages */}
      <div ref={scrollRef} className="agent-chat-messages flex-1 min-h-0 overflow-y-auto px-4 py-6">
        <div className="agent-chat-messages-inner max-w-3xl mx-auto space-y-4">
          {loadingInit && (
            <p className="text-center text-gray-500 mt-12">Chargement…</p>
          )}
          {!loadingInit && conv?.messages?.length === 0 && (
            <p className="text-center text-gray-500 mt-12">
              Démarre la conversation ci-dessous.
            </p>
          )}
          {conv?.messages?.map((m, i) => {
            const isUser = m.role === "user";
            const isLastMessage = i === conv.messages.length - 1;
            const choices = isLastMessage && Array.isArray(m.choices) ? m.choices : [];
            const imagePaths = Array.isArray(m.image_paths) && m.image_paths.length > 0
              ? m.image_paths
              : (m.image_path ? [m.image_path] : []);
            return (
              <div key={i} className={`agent-message-row flex ${isUser ? "justify-end" : "justify-start"}`}>
                <div className={`min-w-0 max-w-2xl ${isUser ? "items-end" : "items-start"} flex flex-col`}>
                  <div
                    className={`agent-message-bubble min-w-0 max-w-full px-4 py-3 rounded-lg whitespace-pre-wrap break-words ${
                      isUser
                        ? "bg-orange-500 text-white"
                        : "bg-white border border-gray-200 text-gray-800"
                    }`}
                  >
                    {m.urgent && (
                      <span className="agent-message-urgent-badge inline-block text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5 mb-1">
                        🚨 Urgent
                      </span>
                    )}
                    <div>{m.content}</div>
                    {imagePaths.map((imagePath, imageIndex) => (
                      <MessageImage
                        key={`${imagePath}-${imageIndex}`}
                        imagePath={imagePath}
                        token={token}
                      />
                    ))}
                  </div>
                  {choices.length > 0 && (
                    <div className="agent-quick-replies mt-2">
                      <div className="flex flex-wrap gap-2">
                        {choices.map((choice) => (
                          <button
                            key={choice}
                            type="button"
                            disabled={sending}
                            onClick={() => sendUserMessage({ message: choice })}
                            className="rounded-full border border-orange-500 px-3 py-1.5 text-sm text-orange-600 hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {choice}
                          </button>
                        ))}
                      </div>
                      <p className="mt-1.5 text-xs text-gray-500">
                        …ou réponds librement ci-dessous
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {sending && (
            <div className="flex justify-start">
              <div className="agent-message-bubble px-4 py-3 rounded-lg bg-white border border-gray-200 text-gray-500 italic">
                L'assistant réfléchit…
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Composer */}
      <div className="agent-composer border-t bg-white px-4 py-3">
        <div className="agent-composer-inner max-w-3xl mx-auto">
          {pendingImages.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2">
              {pendingImages.map((pendingImage, index) => (
                <div key={pendingImage.url} className="relative">
                  <img src={pendingImage.url} alt="à envoyer" className="h-16 max-w-full rounded border" />
                  <button
                    type="button"
                    aria-label={`Retirer la capture ${index + 1}`}
                    onClick={() => removePendingImage(index)}
                    className="absolute -right-1 -top-1 rounded-full bg-white px-1 text-xs text-gray-500 shadow hover:text-red-600"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="agent-composer-controls flex gap-2">
            <label className="agent-attach-button self-end shrink-0 cursor-pointer px-2 py-2 border rounded-lg text-gray-600 hover:bg-gray-50" title="Joindre une image">
              📎
              <input type="file" accept="image/*" multiple className="hidden"
                onChange={(e) => { stageImages(e.target.files); e.target.value = ""; }} />
            </label>
            <button
              type="button"
              onClick={() => setUrgent((u) => !u)}
              title="Marquer ce message comme urgent"
              className={`agent-urgent-toggle self-end shrink-0 px-2 py-2 border rounded-lg transition ${
                urgent
                  ? "bg-red-600 text-white border-red-600"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              🚨
            </button>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              placeholder={sending ? "Envoi en cours…" : "Ton message… (📎 ou colle une capture)"}
              disabled={sending}
              rows={2}
              className="min-w-0 flex-1 border rounded-lg px-3 py-2 text-sm resize-none max-h-[40vh] overflow-y-auto focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <button onClick={onSend} disabled={sending || (!input.trim() && pendingImages.length === 0)}
              className="agent-send-button shrink-0 bg-orange-600 text-white px-4 py-2 rounded-lg disabled:opacity-50 self-end">
              Envoyer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
